<template>
	<div class="input-file">
		<input
			ref="inputEl"
			hidden
			type="file"
			:name
			:multiple
			:accept="mimeTypes.join(',')"
			@change="onFileChange"
		/>

		<slot :trigger-click="onInputClick">
			<UIShButton
				v-if="model.length === max"
				kind="unstyled"
				class="btn-file btn-clear"
				label="Удалить все"
				icon-right="trash"
				@click="handleClear"
			>
				<template #icon-right>
					<UIShIcon name="trash" />
				</template>
			</UIShButton>

			<UIShButton
				v-else
				kind="unstyled"
				class="btn-file btn-trigger"
				label="Добавить файл"
				icon-right="paperclip"
				@click="onInputClick"
			>
				<template #icon-right>
					<UIShIcon name="paperclip" class="icon" />
				</template>
			</UIShButton>
		</slot>

		<ul class="files">
			<li v-for="file in model" :key="file.id" class="media-file">
				<NuxtImg
					v-if="file.type === 'image'"
					class="file-preview file-img"
					:src="file.src"
					:alt="file.src"
					width="40"
					height="40"
					format="webp"
					decoding="async"
				/>

				<div
					v-else
					class="file-preview file-doc"
					:data-type="file.file.type"
					:class="{
						red: file.extension.startsWith('pdf'),
						green: file.extension.startsWith('xl')
					}"
				>
					{{ file.extension }}
				</div>

				<span class="name">{{ file?.file?.name }}</span>

				<UIShLink
					:to="file.src"
					external
					class="preview"
					target="_blank"
				>
					<UIShIcon name="eye" />
				</UIShLink>

				<UIShButtonLabel
					class="btn-delete"
					@click="handleDeleteFile(file.id)"
				>
					<UIShIcon name="x-close" />
				</UIShButtonLabel>
			</li>
		</ul>
	</div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue';

import fileHelpers from '@/utils/file';
import type { UploadFileType, UploadErrorType } from '@/common/types';

type ShInputFileT = {
	max?: number;
	name?: string;
	maxSize?: number;
	disabled?: boolean;
	multiple?: boolean;
	mimeTypes?: string[];
};

const props = withDefaults(defineProps<ShInputFileT>(), {
	maxSize: 200,
	mimeTypes: () => ['*']
});

const model = defineModel<UploadFileType[]>({ required: false, default: [] });
const error = defineModel<UploadErrorType | null>('error');

const inputEl = useTemplateRef('inputEl');

onBeforeUnmount(() => {
	handleClear();
});

function onInputClick() {
	inputEl.value?.click();
}

function validateFile(
	file: File,
	count: number
): { valid: boolean; error: UploadErrorType | null } {
	const mbSize = file.size / 1024 / 1024;
	const validAny = props.mimeTypes.includes('*');
	const validSize = props.maxSize >= mbSize;
	const validType = props.mimeTypes.includes(file.type);

	const validCount =
		props.max !== undefined
			? model.value.length + count <= props.max
			: true;

	if (!validCount) {
		return {
			valid: false,
			error: {
				type: 'count',
				value: `Вы можете прикрепить не более ${props.max} файлов`
			}
		};
	}

	if (!validSize) {
		return {
			valid: false,
			error: {
				type: 'size',
				value: `Размер файла не должен превышать ${props.maxSize} MB`
			}
		};
	}

	if (!validAny && !validType) {
		return {
			valid: false,
			error: { type: 'type', value: 'Неверный тип файла' }
		};
	}

	return { valid: true, error: null };
}

function handleNewFiles(files: File[]) {
	error.value = null;

	for (const file of files) {
		const res = validateFile(file, files.length);

		if (!res.valid) {
			error.value = res.error;
			return;
		}
	}

	const newFiles: UploadFileType[] = files.map((file) => {
		return {
			file: file,
			id: getRandomId(),
			src: URL.createObjectURL(file),
			type: fileHelpers.getTypeByMimetype(file),
			extension: fileHelpers.getExtensionByName(file.name)
		};
	});

	model.value = [...model.value, ...newFiles];
}

function handleClear() {
	error.value = null;
	model.value.forEach((f) => {
		URL.revokeObjectURL(f.src);
	});

	model.value = [];
}

function handleDeleteFile(id: string) {
	const file = model.value.find((f) => f.id === id);
	if (!file) return;

	URL.revokeObjectURL(file.src);
	model.value = model.value.filter((f) => f.id !== id);
}

async function onFileChange(ev: Event) {
	try {
		const target = ev?.target as HTMLInputElement;
		if (!target.files?.length) return;

		handleNewFiles([...target.files]);
	} catch (error) {
		console.error(error);
	} finally {
		// input_el.value.value = null;
	}
}
</script>

<style scoped lang="scss">
.input-file {
	border-radius: 0.889rem;
	background-color: var(--color-gray50);
}

.btn-file {
	display: flex;
	width: 100%;
	flex-wrap: nowrap;
	cursor: pointer;
	overflow: hidden;
	align-items: center;
	gap: 0.667rem;
	padding: 0.889rem;
	border-radius: inherit;
	justify-content: space-between;
	@include useFont(tmds);

	&:hover,
	&:focus,
	&:focus-visible {
		color: var(--color-brand500);
	}
}

.btn-trigger {
	color: var(--color-gray950);

	.icon {
		color: var(--color-brand500);
	}
}

.btn-clear {
	color: var(--color-gray500);
}

.files {
	display: flex;
	flex-direction: column;
	gap: 0.889rem;
	padding: 0.889rem;
	padding-top: 0;

	&:empty {
		display: none;
	}
}

.media-file {
	display: grid;
	align-items: center;
	column-gap: 0.667rem;
	grid-auto-rows: 2.222rem;
	grid-template-columns: 2.222rem 1fr 1.333rem 1.333rem;
	@include useFont(tmdm);
	color: var(--color-gray500);

	.file-preview {
		width: 100%;
		height: 100%;
		border-radius: 0.444rem;

		&.file-img {
			object-fit: cover;
		}

		&.file-doc {
			display: flex;
			align-items: center;
			justify-content: center;
			@include useFont(txsm);
			font-weight: 800;
			color: var(--color-blue);
			background-color: var(--color-white);
			text-transform: uppercase;

			&.red {
				color: var(--color-red);
			}

			&.green {
				color: var(--color-green);
			}
		}
	}

	.name {
		overflow: hidden;
		display: -webkit-box;
		line-clamp: 1;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
		text-overflow: ellipsis;
	}

	.preview {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.btn-delete {
		border-radius: 0.222rem;

		&:hover,
		&:focus,
		&:focus-within,
		&:active {
			color: var(--color-gray500);
		}

		&:focus-visible,
		&:active {
			box-shadow:
				0rem 0rem 0rem 0.111rem var(--color-white),
				0rem 0rem 0rem 0.222rem var(--color-brand500);
		}
	}
}

@include screen1920 {
	.input-file {
		border-radius: 1.111rem;
	}

	.btn-file {
		padding: 1.111rem;
		@include useFont(tlgs);
	}

	.files {
		gap: 1.111rem;
		padding: 1.111rem;
	}

	.media-file {
		@include useFont(tmdm);

		.file-preview {
			&.file-doc {
				@include useFont(txsb);
			}
		}
	}
}
</style>
